const MINT_WRAPPER_CLASS = 'mint-lesson';

function createScriptTags({ id, src }) {
  return new Promise(function (resolve, reject) {
    const s = document.createElement('script');
    let r = false;
    s.type = 'text/javascript';
    s.id = id;
    s.src = src;
    s.onerror = function (err) {
      reject(err, s);
    };
    s.onload = s.onreadystatechange = function () {
      if (!r && (!this.readyState || this.readyState === 'complete')) {
        r = true;
        resolve();
      }
    };
    const t = document.getElementsByTagName('script')[0];
    t.parentElement.insertBefore(s, t);
  });
}

export function buildMathJaxHubShim(ready) {
  // Shim de la API v2 `MathJax.Hub` que v3 no tiene. Mint/Lemonade la llaman en cuanto ven
  // window.MathJax (que fijamos síncronamente), posiblemente ANTES de que v3 haya cargado.
  // Por eso `ready` es una promesa: encolamos las llamadas hasta que v3 termina su startup y
  // entonces las traducimos a typesetPromise. Así su try/catch nunca se traga una excepción.
  function typeset(elements, callback) {
    const nodes = elements == null ? undefined : elements.nodeType ? [elements] : Array.prototype.slice.call(elements);
    const promise = ready.then(() => window.MathJax.typesetPromise(nodes));
    return typeof callback === 'function' ? promise.then(callback) : promise;
  }

  return {
    Typeset: typeset,
    Config: function () {},
    Register: { MessageHook: function () {}, StartupHook: function () {}, LoadHook: function () {} },
    Queue: function () {
      return Array.prototype.slice.call(arguments).reduce(
        (chain, spec) =>
          chain.then(() => {
            if (typeof spec === 'function') return spec();
            if (Array.isArray(spec)) {
              const [method, context, ...rest] = spec;
              const fn = typeof method === 'function' ? method : context[method];
              return fn.apply(context, rest);
            }
          }),
        ready
      );
    },
  };
}

function createLinkTags({ id, src }) {
  return new Promise(function (resolve, reject) {
    const lnk = document.createElement('link');
    lnk.href = src;
    lnk.rel = 'stylesheet';
    lnk.type = 'text/css';
    (document.head || document.documentElement).appendChild(lnk);

    let r = false;

    lnk.onerror = function (err) {
      reject(err, lnk);
    };
    lnk.onload = lnk.onreadystatechange = function () {
      if (!r && (!this.readyState || this.readyState === 'complete')) {
        r = true;
        resolve();
      }
    };
    const t = document.getElementsByTagName('link')[0];
    t.parentElement.insertBefore(lnk, t);
  });
}

export function addMathJax() {
  if (!window || window.MathJax || window.loadingMathLib) return Promise.resolve();

  // Reclamamos el global de forma síncrona (antes de cargar el script) para que el
  // addMathJax() propio de Mint/Lemonade lo vea ocupado y no cargue su CDN 2.7.7.
  window.loadingMathLib = true;

  // Promesa que resolvemos cuando v3 termina su startup; el Hub provisional encola sobre ella.
  let markReady;
  const ready = new Promise((resolve) => (markReady = resolve));

  window.MathJax = {
    loader: { load: ['input/asciimath', '[tex]/color', '[tex]/colorv2', '[tex]/cancel'] },
    options: { ignoreHtmlClass: 'no-mathjax|editor-page', processHtmlClass: 'fr-math', enableMenu: false },
    tex: { packages: { '[+]': ['color', 'colorv2', 'cancel'] }, inlineMath: [['\\(', '\\)']], autoload: { color: [] } },
    chtml: { displayAlign: 'left' },
    startup: {
      typeset: false,
      ready: () => {
        window.MathJax.startup.defaultReady();
        // Reafirmamos el Hub (por si v3 lo reemplazó al arrancar) ya con la promesa resuelta.
        window.MathJax.Hub = buildMathJaxHubShim(Promise.resolve());
        markReady();
      },
    },
    // Hub v2 provisional: disponible de forma síncrona; encola hasta que v3 esté listo.
    Hub: buildMathJaxHubShim(ready),
    Callback: (fn) => fn,
  };

  // v3 servido desde package.json (copiado a /mathjax por Rsbuild), no CDN. El id
  // `MathJax-script` es necesario para que el loader de v3 resuelva su ruta base (fuentes,
  // input jaxes) relativa a /mathjax.
  const src = `${process.env.PUBLIC_URL || ''}/mathjax/tex-mml-chtml.js`;
  return createScriptTags({ id: 'MathJax-script', src }).catch((e) => {
    console.log('error cargando Mathjax', e);
    // Si el script no carga, `startup.ready` nunca corre y `markReady()` nunca se llama:
    // revertimos el estado para no dejar el shim colgado (las llamadas encoladas rechazan en
    // vez de quedar pendientes para siempre) y permitir un reintento posterior.
    window.loadingMathLib = false;
    delete window.MathJax;
    markReady();
  });
}

function addAutoRenderKatex() {
  const autoRendercdnPath = 'https://cdn.jsdelivr.net/npm/katex@0.15.1/dist/contrib/auto-render.min.js';
  if (window && !window.renderMathInElement) {
    return createScriptTags({ id: 'Katex-auto-render', src: autoRendercdnPath });
  } else {
    return Promise.resolve();
  }
}

function addKatex() {
  const cdnPath = 'https://cdn.jsdelivr.net/npm/katex@0.15.1/dist/katex.min.js';
  if (window && !window.katex) {
    return createScriptTags({ id: 'Katex-script', src: cdnPath });
  } else {
    return Promise.resolve();
  }
}

function addKatexCss() {
  const cdnStylePath = 'https://cdn.jsdelivr.net/npm/katex@0.15.1/dist/katex.min.css';
  createLinkTags({ id: 'Katex-style', src: cdnStylePath });
}

function firstRender() {
  const lemonadeContainer = document.getElementsByClassName(MINT_WRAPPER_CLASS);
  if (lemonadeContainer.length > 0)
    if (window.renderMathInElement) {
      window.renderMathInElement(lemonadeContainer[0]);
    } else if (window.MathJax?.typesetPromise) {
      window.MathJax.typesetPromise([lemonadeContainer[0]]);
    }
}

export const MATH_RENDER_ENGINE_TANGERINE = 'mathjax'; // katex, mathjax
export const MATH_RENDER_ENGINE_BACKOFFICE = 'katex'; // katex, mathjax

export function addLatex(mathEngine) {
  console.log('ADD MATH ENGINE BackOffice ', mathEngine);
  if (mathEngine === 'katex') {
    addKatex().then(() => {
      addKatexCss();
      addAutoRenderKatex().then(firstRender);
    });
  } else {
    addMathJax().then(firstRender);
  }
}
